import React, { useEffect, useRef, useState } from 'react';
import AudioVisualizer from './AudioVisualizer';

interface AudioPlayerProps {
  youtubeUrl: string;
  startTime: number;
  cancionNumber: number;
  totalCanciones: number;
  isVisible?: boolean;
  onAudioStateChange?: (isPlaying: boolean, currentTime: number, action: string) => void;
  onPlayerReady?: () => void;
  shouldPause?: boolean;
  hideVideo?: boolean;
  isResolved?: boolean;
  defaultVolume?: number;
  showParticipantControls?: boolean;
}

const checkIsMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  youtubeUrl,
  startTime,
  cancionNumber,
  totalCanciones,
  isVisible = true,
  onAudioStateChange,
  onPlayerReady,
  shouldPause = false,
  hideVideo = false,
  isResolved = false,
  defaultVolume = 70,
  showParticipantControls = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(defaultVolume);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [lastVolumeBeforeMute, setLastVolumeBeforeMute] = useState(defaultVolume > 0 ? defaultVolume : 50);
  const [isMobile] = useState(checkIsMobile);
  const [dots, setDots] = useState('');

  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const bufferingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getVideoId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const cleanup = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (bufferingTimeoutRef.current) {
      clearTimeout(bufferingTimeoutRef.current);
      bufferingTimeoutRef.current = null;
    }
    if (playerRef.current && typeof playerRef.current.destroy === 'function') {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying player:', e);
      }
      playerRef.current = null;
    }
  };

  const updateProgress = () => {
    if (!playerRef.current || !mountedRef.current) return;
    try {
      const time = playerRef.current.getCurrentTime() || 0;
      const dur = playerRef.current.getDuration() || 0;
      setCurrentTime(time);
      if (dur > 0 && Math.abs(dur - duration) > 0.1) {
        setDuration(dur);
      }
    } catch (error) {
      console.warn('Error updating progress:', error);
    }
  };

  const loadYouTubeAPI = (): Promise<void> => {
    return new Promise((resolve) => {
      if ((window as any).YT?.Player) {
        resolve();
        return;
      }
      if ((window as any).YTApiLoading) {
        const checkAPI = () => {
          if ((window as any).YT?.Player) {
            resolve();
          } else {
            setTimeout(checkAPI, 100);
          }
        };
        checkAPI();
        return;
      }
      (window as any).YTApiLoading = true;
      (window as any).onYouTubeIframeAPIReady = () => {
        (window as any).YTApiLoading = false;
        resolve();
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    });
  };

  const initPlayer = async () => {
    if (isMobile) {
      let meta = document.querySelector('meta[name="referrer"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'referrer');
        meta.setAttribute('content', 'no-referrer-when-downgrade');
        document.head.appendChild(meta);
      }
    }

    const videoId = getVideoId(youtubeUrl);
    if (!videoId) {
      setError('URL de YouTube inválida');
      setIsLoading(false);
      return;
    }
    if (!containerRef.current || !mountedRef.current) return;

    try {
      await loadYouTubeAPI();
      cleanup();

      playerRef.current = new (window as any).YT.Player(containerRef.current, {
        height: '200',
        width: '100%',
        videoId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          start: Math.floor(startTime),
          origin: window.location.origin,
        },
        events: {
          onReady: (event: any) => {
            if (!mountedRef.current) return;
            event.target.setVolume(volume);
            event.target.seekTo(startTime, true);
            const dur = event.target.getDuration();
            if (dur > 0) setDuration(dur);
            setPlayerReady(true);
            setIsLoading(false);
            setError(null);
            try { event.target.playVideo(); } catch (e) { }
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = setInterval(updateProgress, 250);
            onPlayerReady?.();
          },
          onStateChange: (event: any) => {
            if (!mountedRef.current) return;
            const YT = (window as any).YT;
            const state = event.data;
            const playing = state === YT.PlayerState.PLAYING;
            const paused = state === YT.PlayerState.PAUSED;
            const buffering = state === YT.PlayerState.BUFFERING;
            const ended = state === YT.PlayerState.ENDED;
            setIsPlaying(playing);
            if (ended) {
              setHasEnded(false);
              setIsPlaying(false);
              try {
                playerRef.current.seekTo(startTime, true);
                playerRef.current.playVideo();
              } catch (e) { }
            }
            let action = 'unknown';
            if (ended) action = 'ended';
            else if (playing) action = 'play';
            else if (paused) action = 'pause';
            else if (buffering) action = 'buffering';
            const time = playerRef.current?.getCurrentTime?.() || 0;
            onAudioStateChange?.(playing, time, action);
            if (buffering) {
              if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
              bufferingTimeoutRef.current = setTimeout(() => {
                if (playerRef.current && mountedRef.current) {
                  const currentState = playerRef.current.getPlayerState();
                  if (currentState === YT.PlayerState.BUFFERING) {
                    try {
                      const currentTime = playerRef.current.getCurrentTime();
                      playerRef.current.seekTo(currentTime, true);
                      setTimeout(() => {
                        if (playerRef.current && mountedRef.current) {
                          try { playerRef.current.playVideo(); } catch (e) { }
                        }
                      }, 1000);
                    } catch (e) { }
                  }
                }
              }, 3000);
            } else {
              if (bufferingTimeoutRef.current) {
                clearTimeout(bufferingTimeoutRef.current);
                bufferingTimeoutRef.current = null;
              }
            }
          },
          onError: (event: any) => {
            if (event.data === 150) {
              setError('Esta canción está bloqueada para reproducción embebida');
            } else if (event.data === 153) {
              setError('Error de configuración (153). Intenta usar HTTPS, recargar o desactivar bloqueadores de publicidad.');
            } else {
              setError(`Error del reproductor: ${event.data}`);
            }
            setIsLoading(false);
          },
        },
      });
    } catch (error) {
      setError('Error al crear el reproductor');
      setIsLoading(false);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current || !playerReady || !duration || isResolved || !showParticipantControls) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    try {
      if (hasEnded) {
        setHasEnded(false);
      }
      playerRef.current.seekTo(newTime, true);
      playerRef.current.playVideo();
      setCurrentTime(newTime);
      onAudioStateChange?.(true, newTime, 'seek');
    } catch (error) { }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setLastVolumeBeforeMute(newVolume);
    }
    if (playerRef.current && playerReady) {
      try { playerRef.current.setVolume(newVolume); } catch (error) { }
    }
  };

  const togglePlay = () => {
    if (!playerRef.current || !playerReady) return;
    try {
      if (isPlaying) {
        playerRef.current.pauseVideo();
        setIsPlaying(false);
      } else {
        if (hasEnded) {
          setHasEnded(false);
        }
        playerRef.current.playVideo();
        setIsPlaying(true);
      }
    } catch (error) { }
  };

  const toggleMute = () => {
    if (!playerRef.current || !playerReady) return;
    try {
      if (volume === 0) {
        const restoredVolume = lastVolumeBeforeMute > 0 ? lastVolumeBeforeMute : 50;
        setVolume(restoredVolume);
        playerRef.current.setVolume(restoredVolume);
      } else {
        setLastVolumeBeforeMute(volume);
        setVolume(0);
        playerRef.current.setVolume(0);
      }
    } catch (error) { }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  useEffect(() => {
    setHasEnded(false);
    setIsPlaying(false);
    const videoId = getVideoId(youtubeUrl);
    if (!videoId) {
      setError('URL de YouTube inválida');
      setIsLoading(false);
      return;
    }

    if (playerRef.current && playerReady) {
      try {
        setIsLoading(true);
        setCurrentTime(0);
        setDuration(0);
        playerRef.current.loadVideoById({
          videoId: videoId,
          startSeconds: Math.floor(startTime),
          suggestedQuality: 'tiny',
        });
        setIsLoading(false);
        setError(null);
        return;
      } catch (e) {
        console.warn('Re-initializing player due to loadVideoById error:', e);
      }
    }

    initPlayer();
    // eslint-disable-next-line
  }, [youtubeUrl]);

  // Dynamic user gesture trigger for mobile devices to autoplay audio with sound
  useEffect(() => {
    if (!playerReady || isPlaying || isResolved || !isMobile) return;

    // 1. Try playing video immediately
    if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
      try {
        playerRef.current.playVideo();
      } catch (e) {
        console.warn('Initial autoplay attempt failed:', e);
      }
    }

    // 2. Setup document-wide listeners to unlock audio on the next user interaction
    const unlockAudio = () => {
      if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
        try {
          playerRef.current.playVideo();
        } catch (e) {
          console.warn('Could not auto-start video on user interaction:', e);
        }
      }
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, [playerReady, isPlaying, isResolved, isMobile, youtubeUrl]);

  // Loading dots sequential animation
  useEffect(() => {
    if (isPlaying) {
      setDots('');
      return;
    }
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    if (isResolved && playerRef.current && playerReady && isPlaying) {
      try {
        playerRef.current.pauseVideo();
        onAudioStateChange?.(false, currentTime, 'resolved');
      } catch (error) { }
    }
  }, [isResolved, playerReady, isPlaying, currentTime, onAudioStateChange]);

  useEffect(() => {
    if (!playerRef.current || !playerReady || isResolved || hasEnded) return;
    try {
      if (shouldPause && isPlaying) {
        playerRef.current.pauseVideo();
      } else if (!shouldPause && !isPlaying) {
        playerRef.current.playVideo();
      }
    } catch (error) { }
  }, [shouldPause, playerReady, isPlaying, isResolved, hasEnded]);

  useEffect(() => {
    setVolume(defaultVolume);
    if (playerRef.current && playerReady) {
      try { playerRef.current.setVolume(defaultVolume); } catch (error) { }
    }
  }, [defaultVolume, playerReady]);

  if (!isVisible) {
    return (
      <div style={{ display: 'none' }}>
        <div ref={containerRef} />
      </div>
    );
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="rounded-lg p-6"
      style={{
        background: 'linear-gradient(135deg, rgba(10, 10, 15, 0.98), rgba(22, 33, 62, 0.92), rgba(15, 52, 96, 0.9), rgba(10, 10, 15, 0.98))',
        boxShadow: '0 0 24px rgba(0, 255, 136, 0.28), inset 0 0 28px rgba(0, 255, 136, 0.08), 0 0 36px rgba(0, 255, 255, 0.14)',
      }}
    >
      {/* Player de YouTube */}
      {!hideVideo && (
        <div className="mb-4">
          <div ref={containerRef} className="w-full bg-black rounded-lg overflow-hidden" style={{ height: '200px' }} />
        </div>
      )}

      {/* Player oculto cuando hideVideo=true */}
      {hideVideo && (
        <div style={{ display: 'none' }}>
          <div ref={containerRef} className="w-full bg-black rounded-lg overflow-hidden" style={{ height: '200px' }} />
        </div>
      )}

      {/* Visualizador cuando video está oculto */}
      {hideVideo && (
        <AudioVisualizer
          isPlaying={isPlaying}
          className="mb-4"
        />
      )}

      {/* Estado de carga */}
      {isLoading && !isMobile && (
        <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(0, 255, 255, 0.08)', border: '1px solid rgba(0, 255, 255, 0.35)' }}>
          <p className="text-cyan-200 text-center">
            <span className="inline-block animate-spin mr-2">⏳</span>
            Cargando reproductor...
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(255, 0, 80, 0.14)', border: '1px solid rgba(255, 0, 120, 0.45)' }}>
          <p className="text-red-200 text-center">
            <span className="mr-2">❌</span>
            {error}
          </p>
        </div>
      )}

      {/* Controles */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {/* Barra de progreso con botón de reproducir/pausar en móvil */}
          {showParticipantControls && !isResolved && (
            <div className="flex items-center space-x-3">
              <div
                className="relative flex-1 h-4 rounded-full overflow-hidden cursor-pointer"
                style={{ background: 'rgba(5, 25, 40, 0.85)', border: '1px solid rgba(0, 255, 136, 0.35)' }}
                onClick={handleProgressClick}
              >
                <div
                  className="h-full rounded-full transition-all duration-100 ease-linear"
                  style={{
                    background: 'linear-gradient(90deg, #00ff88, #00e5ff, #00ff88)',
                    boxShadow: '0 0 10px rgba(0, 255, 136, 0.5)',
                    width: duration > 0 ? `${Math.max(0, Math.min(100, (currentTime / duration) * 100))}%` : '0%',
                  }}
                />
              </div>
            </div>
          )}

          {/* Control de volumen (oculto en móvil) */}
          {!isMobile && (
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={toggleMute}
                disabled={!playerReady}
                className="text-cyan-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={volume === 0 ? 'Activar sonido' : 'Silenciar'}
                title={volume === 0 ? 'Activar sonido' : 'Silenciar'}
              >
                {volume === 0 ? '🔇' : '🔊'}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                disabled={!playerReady}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed"
                style={{ background: 'rgba(0, 255, 136, 0.16)' }}
              />
              <span className="text-cyan-100 text-sm w-8">{volume}%</span>
            </div>
          )}
        </div>
      )}

      {/* Mensaje de carga para móviles */}
      {isMobile && !isPlaying && !error && !isResolved && (
        <div className="mt-4 text-center animate-pulse">
          <p className="font-gaming text-gaming-cyan text-sm tracking-wider">
            Pulsa para reproducir{dots}
          </p>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
